import { createFileRoute, useRouterState } from '@tanstack/react-router'

import { redirectIfAuthenticated } from '@/lib/auth/guards'
import AuthLayout from '@/screens/auth/components/AuthLayout'

/**
 * Shared parent for /signin and /signup.
 *
 * Contributes no URL segment, so both paths are unchanged. Its whole purpose is
 * to own the hero panel: as sibling routes the two screens shared no component
 * instance, so every tab switch unmounted the panel and rebuilt it — a new
 * <aside>, a new <img>, and a fresh decode of a 200 KB mascot. Under one parent
 * the panel simply stays mounted.
 */
function AuthLayoutRoute() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  return <AuthLayout active={pathname.startsWith('/signup') ? 'signup' : 'signin'} />
}

/**
 * `beforeLoad` on the layout, so both /signin and /signup are covered by one
 * statement: a member who still has a session has no business on either.
 *
 * It does not fight the sign-in navigation. The guard reads the auth store, and
 * the router does not re-run `beforeLoad` because a store outside it changed —
 * it runs on navigation. So the sequence is: the form signs in, then navigates
 * to the `redirect` search param (or home), and this guard has already had its
 * say on the way in. The member never sees the screen it guards re-evaluate
 * underneath them.
 *
 * The one thing it gives up is the `redirect` param on a stale /signin link
 * followed by a member who is already signed in: `redirectIfAuthenticated`
 * sends them home rather than on to the deep link they had. Honouring it would
 * be a change to the shared guard, which this route does not own.
 *
 * /forgot-password is the exception. A signed-in member who has forgotten
 * their password cannot use Change password, which asks for it, and Account ›
 * Security's "Set a password" sends a social account here on purpose (P3).
 * The form ends the tab's session itself once mm-be has reset the password.
 */
export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ location }) =>
    location.pathname === '/forgot-password' ? undefined : redirectIfAuthenticated(),
  component: AuthLayoutRoute,
})
